import inRange from 'lodash/inRange'

export const toBMapPoint = (arg) => {
	if (!arg || !window.BMap) {
		return
	}
	return new window.BMap.Point(arg.lng, arg.lat)
}

export const toBMapBounds = (arg) => {
	if (!arg || !window.BMap) {
		return
	}
	return new window.BMap.Bounds(toBMapPoint(arg.sw), toBMapPoint(arg.ne))
}

/**
 * @description Returns the distance, in meters, between two LatLngs.
 * @param {{lat: Number, lng: Number}} from Latitude and longitude of the first point
 * @param {{lat: Number, lng: Number}} to Latitude and longitude of the second point
 * @param {'google'|'baidu'} provider Map provider
 * @param {React.Ref} mapRef Baidu map reference
 * @returns {Number} Distance in meters
 * @example getDistance({lat, lng}, {lat, lng});
 * @reference https://developers.google.com/maps/documentation/javascript/reference/geometry#spherical.computeDistanceBetween
 */
export const getDistance = (from, to, provider = 'google', mapRef) => {
	if (provider === 'google' && window?.google?.maps) {
		let fromA = new window.google.maps.LatLng(from.lat, from.lng)
		let toB = new window.google.maps.LatLng(to.lat, to.lng)
		return window.google.maps.geometry.spherical.computeDistanceBetween(fromA, toB)
	}

	if (provider === 'baidu' && window?.BMap && mapRef?.current) {
		return mapRef.current?.getDistance(
			toBMapPoint({ lat: from.lat, lng: from.lng }),
			toBMapPoint({ lat: to.lat, lng: to.lng })
		)
	}
}

/**
 * @description Returns if the distance is contained in the passed radius value.
 * @param {Number} distance Distance in meters from two points
 * @param {Number} radius Radius in meters
 * @returns {Boolean} true || false
 * @example distanceInRadius(X, Y);
 */
export const distanceInRadius = (distance, radius = 15000) => {
	return distance < radius && distance > 0
}

/**
 * @description Converts a distance from meters to kilometers.
 * @param {Number} distance Distance expressed in meters
 * @returns {String} Distance in km
 */
export const distanceInKm = (distance) => {
	return (distance / 1000).toFixed(1)
}

/**
 * @description Returns a new array of stores sorted by distance.
 * @param {Array.<object>} stores Stores data
 * @returns {Array.<object>} Sorted array
 */
export const sortStoresByDistance = (stores) => {
	stores.sort((a, b) => {
		return a?.distance - b?.distance
	})
}

/**
 * @description Given a list of stores with coordinates,
 * get map bounds from all stores coordinates.
 * @param {Array} stores - stores
 * @param {String} provider - Map provider. Default is 'google'
 * @returns
 */
export const getMapBounds = (stores, provider = 'google') => {
	if (provider === 'google' && window?.google?.maps) {
		let bounds = new window.google.maps.LatLngBounds()
		stores?.forEach((store) => {
			bounds.extend(new window.google.maps.LatLng(store.latitude, store.longitude))
		})
		return bounds
	}

	if (provider === 'baidu' && window?.BMap) {
		let bMapBounds = new window.BMap.Bounds()
		stores?.forEach((store) => {
			bMapBounds.extend(toBMapPoint({ lat: store.latitude, lng: store.longitude }))
		})
		return bMapBounds
	}
}

/**
 * @description Generate points to use clusters in Google Maps.
 * @param {object} store - Store data
 * @returns {{
 * type: 'Feature',
 * properties: {cluster: boolean, pointId: number | string},
 * geometry: {
 * 	type: 'Point',
 * 	coordinates: [longitude: number, latitude: number]
 * },
 * store: {object},
 * id: number | string
 * }} Point data
 * @reference https://datatracker.ietf.org/doc/html/rfc7946#appendix-A
 */
export const generateClusterPoint = (store) => ({
	type: 'Feature',
	properties: { cluster: false, pointId: store.id },
	geometry: {
		type: 'Point',
		coordinates: [store.longitude, store.latitude],
	},
	store: store,
	id: store.id,
})

/**
 * @description Detect if coordinates are valid.
 *
 * Latitude must be a value between -90 and 90.
 * Longitude must be a value between -180 and 180.
 * @param {{lat: number, lng: number}} coordinates - Coordinates
 * @returns {Boolean} true || false
 */
export const isValidCoordinates = (coordinates) => {
	const { lat, lng } = coordinates || {}
	return lat && lng && inRange(lat, -90, 90) && inRange(lng, -180, 180)
}

export const isArraysEqualEps = (arrayA, arrayB, eps = 0.00001) => {
	if (arrayA && arrayB) {
		for (let i = 0; i !== arrayA.length; ++i) {
			if (Math.abs(arrayA[i] - arrayB[i]) > eps) {
				return false
			}
		}
		return true
	}
	return false
}
